import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "../../../utils/axios";
import { SERVER_URL } from "../../../constants/production";

export interface Interview {
  _id: string;
  candidate: { _id: string; name: string; email: string };
  status: string;
  interviewType: string;
  confirmedSlotStart?: string;
  confirmedSlotEnd?: string;
  durationMinutes: number;
  meetingLink?: string;
  interviewerDetails?: Array<{ user: { name: string; email: string } }>;
}

export interface IntegrationStatus {
  hasIntegration: boolean;
  provider?: string;
  status?: string;
}

interface InterviewsState {
  list: Interview[];
  integration: IntegrationStatus | null;
  loading: boolean;
  error: string | null;
}

const initialState: InterviewsState = {
  list: [],
  integration: null,
  loading: false,
  error: null,
};

// Use unified SERVER_URL to avoid process is not defined errors in WDS
const API_BASE = `${SERVER_URL}/api`;

export const fetchInterviews = createAsyncThunk(
  "interviews/fetchInterviews",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/api/interviews`, {
        withCredentials: true,
      });
      // Backend uses responseStandardizer which wraps output in { success, data, message, timestamp }
      const payload = response.data;
      return payload.data || payload.interviews || payload;
    } catch (error) {
      // @ts-ignore
      return rejectWithValue(error.response?.data || "Failed to fetch interviews");
    }
  }
);

export const fetchIntegrationStatus = createAsyncThunk(
  "interviews/fetchIntegrationStatus",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/api/interviews/integration`, {
        withCredentials: true,
      });
      const payload = response.data;
      const integrationData = payload.data !== undefined ? payload.data : payload;

      // Inject hasIntegration for UI backward compatibility
      if (integrationData && integrationData.provider) {
        return { ...integrationData, hasIntegration: true };
      }
      return integrationData;
    } catch (error) {
      // @ts-ignore
      return rejectWithValue(error.response?.data || "Failed to fetch integration status");
    }
  }
);

export const scheduleInterview = createAsyncThunk(
  "interviews/scheduleInterview",
  async (payload: any, { rejectWithValue }) => {
    try {
      const endpoint = payload.mode === 'automated' ? '/api/interviews/automated/send-invite' : '/api/interviews/manual';
      const response = await axios.post(endpoint, payload, {
        withCredentials: true,
      });
      return {
        interview: response.data.interview || response.data,
        calendarInviteSent: !!response.data.calendarInviteSent,
        emailSent: !!response.data.emailSent,
      };
    } catch (error) {
      // @ts-ignore
      const err = error;
      return rejectWithValue(
        err?.response?.data?.userMessage ||
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to schedule interview"
      );
    }
  }
);

const interviewsSlice = createSlice({
  name: "interviews",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // fetchInterviews
    builder.addCase(fetchInterviews.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchInterviews.fulfilled, (state, action: PayloadAction<Interview[]>) => {
      state.loading = false;
      state.list = action.payload;
    });
    builder.addCase(fetchInterviews.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload ? String(action.payload) : "Failed to fetch interviews";
    });

    // fetchIntegrationStatus
    builder.addCase(fetchIntegrationStatus.fulfilled, (state, action) => {
      state.integration = action.payload;
    });

    // scheduleInterview
    builder.addCase(scheduleInterview.fulfilled, (state, action) => {
      // Optimistically add it to the list if it's confirmed or awaiting
      if (action.payload?.interview) {
        state.list.push(action.payload.interview);
      }
    });
  },
});

export default interviewsSlice.reducer;
